// List <-> map hover (#3077) as an external store rather than Dashboard state.
//
// Hover changes on every card the pointer crosses. Held in Dashboard state,
// each change re-rendered the Dashboard, rebuilt the list's row context and
// re-rendered every visible card. Here a change notifies subscribers only:
// each card reads `useIsHovered(store, id)`, so just the card that gained
// hover and the one that lost it re-render, and the map redraws its ring
// imperatively.

import { useSyncExternalStore } from "react";

export interface HoverStore {
  get(): string | null;
  set(id: string | null): void;
  subscribe(listener: () => void): () => void;
}

export function createHoverStore(): HoverStore {
  let current: string | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    set: (id) => {
      if (id === current) return;
      current = id;
      listeners.forEach((l) => l());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const noopSubscribe = () => () => {};

/** True while `id` is the hovered property. Re-renders only when that flips. */
export function useIsHovered(store: HoverStore | undefined, id: string): boolean {
  return useSyncExternalStore(
    store ? store.subscribe : noopSubscribe,
    () => (store ? store.get() === id : false)
  );
}
